"use client"

import { useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Copy, RefreshCw, Download, Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"

type FieldType =
  | "uuid"
  | "firstName"
  | "lastName"
  | "fullName"
  | "email"
  | "phone"
  | "address"
  | "city"
  | "country"
  | "zipCode"
  | "company"
  | "jobTitle"
  | "username"
  | "password"
  | "date"
  | "pastDate"
  | "futureDate"
  | "number"
  | "boolean"
  | "url"
  | "avatar"
  | "paragraph"
  | "sentence"
  | "word"
  | "creditCard"
  | "color"
  | "ipAddress"

interface Field {
  id: string
  name: string
  type: FieldType
}

type OutputFormat = "json" | "csv" | "sql"

// Simple random data generators
const generators = {
  uuid: () => crypto.randomUUID(),
  firstName: () => {
    const names = ["James", "Mary", "John", "Patricia", "Robert", "Jennifer", "Michael", "Linda", "David", "Elizabeth", "William", "Barbara", "Richard", "Susan", "Joseph", "Jessica", "Thomas", "Sarah", "Christopher", "Karen", "Emma", "Olivia", "Ava", "Sophia", "Isabella", "Liam", "Noah", "Oliver", "Elijah", "Lucas"]
    return names[Math.floor(Math.random() * names.length)]
  },
  lastName: () => {
    const names = ["Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis", "Rodriguez", "Martinez", "Hernandez", "Lopez", "Gonzalez", "Wilson", "Anderson", "Thomas", "Taylor", "Moore", "Jackson", "Martin", "Lee", "Perez", "Thompson", "White", "Harris"]
    return names[Math.floor(Math.random() * names.length)]
  },
  fullName: () => `${generators.firstName()} ${generators.lastName()}`,
  email: () => {
    const domains = ["gmail.com", "yahoo.com", "outlook.com", "hotmail.com", "example.com", "mail.com"]
    return `${generators.firstName().toLowerCase()}.${generators.lastName().toLowerCase()}${Math.floor(Math.random() * 100)}@${domains[Math.floor(Math.random() * domains.length)]}`
  },
  phone: () => `+1-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 9000 + 1000)}`,
  address: () => {
    const streets = ["Main St", "Oak Ave", "Park Blvd", "Maple Dr", "Cedar Ln", "Pine Rd", "Elm St", "Washington Ave", "Lake Dr", "Hill Rd"]
    return `${Math.floor(Math.random() * 9999 + 1)} ${streets[Math.floor(Math.random() * streets.length)]}`
  },
  city: () => {
    const cities = ["New York", "Los Angeles", "Chicago", "Houston", "Phoenix", "Philadelphia", "San Antonio", "San Diego", "Dallas", "San Jose", "Austin", "Jacksonville", "Fort Worth", "Columbus", "Charlotte", "Seattle", "Denver", "Boston", "Nashville", "Portland"]
    return cities[Math.floor(Math.random() * cities.length)]
  },
  country: () => {
    const countries = ["United States", "Canada", "United Kingdom", "Germany", "France", "Australia", "Japan", "Italy", "Spain", "Netherlands", "Sweden", "Norway", "Denmark", "Switzerland", "Ireland"]
    return countries[Math.floor(Math.random() * countries.length)]
  },
  zipCode: () => String(Math.floor(Math.random() * 90000 + 10000)),
  company: () => {
    const prefixes = ["Tech", "Global", "Dynamic", "Innovative", "Creative", "Digital", "Smart", "Future", "Prime", "Elite"]
    const suffixes = ["Solutions", "Systems", "Industries", "Technologies", "Corp", "Inc", "Labs", "Group", "Ventures", "Partners"]
    return `${prefixes[Math.floor(Math.random() * prefixes.length)]} ${suffixes[Math.floor(Math.random() * suffixes.length)]}`
  },
  jobTitle: () => {
    const titles = ["Software Engineer", "Product Manager", "Data Scientist", "UX Designer", "DevOps Engineer", "Marketing Manager", "Sales Representative", "HR Specialist", "Financial Analyst", "Project Manager", "QA Engineer", "Frontend Developer", "Backend Developer", "Full Stack Developer", "System Administrator"]
    return titles[Math.floor(Math.random() * titles.length)]
  },
  username: () => `${generators.firstName().toLowerCase()}${Math.floor(Math.random() * 1000)}`,
  password: () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%"
    return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join("")
  },
  date: () => {
    const start = new Date(2020, 0, 1)
    const end = new Date()
    return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime())).toISOString().split("T")[0]
  },
  pastDate: () => {
    const now = new Date()
    const past = new Date(now.getTime() - Math.random() * 365 * 24 * 60 * 60 * 1000 * 3)
    return past.toISOString().split("T")[0]
  },
  futureDate: () => {
    const now = new Date()
    const future = new Date(now.getTime() + Math.random() * 365 * 24 * 60 * 60 * 1000)
    return future.toISOString().split("T")[0]
  },
  number: () => Math.floor(Math.random() * 10000),
  boolean: () => Math.random() > 0.5,
  url: () => `https://${generators.word().toLowerCase()}.example.com/${generators.word().toLowerCase()}`,
  avatar: () => `https://i.pravatar.cc/150?u=${Math.random().toString(36).substring(7)}`,
  paragraph: () => {
    const sentences = Array.from({ length: Math.floor(Math.random() * 3 + 3) }, () => generators.sentence())
    return sentences.join(" ")
  },
  sentence: () => {
    const words = Array.from({ length: Math.floor(Math.random() * 8 + 5) }, () => generators.word())
    words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1)
    return words.join(" ") + "."
  },
  word: () => {
    const words = ["lorem", "ipsum", "dolor", "sit", "amet", "consectetur", "adipiscing", "elit", "sed", "do", "eiusmod", "tempor", "incididunt", "labore", "dolore", "magna", "aliqua", "enim", "minim", "veniam", "quis", "nostrud", "exercitation", "ullamco", "laboris"]
    return words[Math.floor(Math.random() * words.length)]
  },
  creditCard: () => {
    const prefix = ["4", "5", "3"][Math.floor(Math.random() * 3)]
    return prefix + Array.from({ length: 15 }, () => Math.floor(Math.random() * 10)).join("")
  },
  color: () => "#" + Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0"),
  ipAddress: () => Array.from({ length: 4 }, () => Math.floor(Math.random() * 256)).join("."),
}

const fieldTypes: { value: FieldType; label: string; category: string }[] = [
  { value: "uuid", label: "UUID", category: "ID" },
  { value: "firstName", label: "First Name", category: "Person" },
  { value: "lastName", label: "Last Name", category: "Person" },
  { value: "fullName", label: "Full Name", category: "Person" },
  { value: "email", label: "Email", category: "Person" },
  { value: "phone", label: "Phone", category: "Person" },
  { value: "username", label: "Username", category: "Person" },
  { value: "password", label: "Password", category: "Person" },
  { value: "avatar", label: "Avatar URL", category: "Person" },
  { value: "address", label: "Street Address", category: "Address" },
  { value: "city", label: "City", category: "Address" },
  { value: "country", label: "Country", category: "Address" },
  { value: "zipCode", label: "Zip Code", category: "Address" },
  { value: "company", label: "Company", category: "Business" },
  { value: "jobTitle", label: "Job Title", category: "Business" },
  { value: "date", label: "Date", category: "Date" },
  { value: "pastDate", label: "Past Date", category: "Date" },
  { value: "futureDate", label: "Future Date", category: "Date" },
  { value: "number", label: "Number", category: "Data" },
  { value: "boolean", label: "Boolean", category: "Data" },
  { value: "url", label: "URL", category: "Data" },
  { value: "color", label: "Color (Hex)", category: "Data" },
  { value: "ipAddress", label: "IP Address", category: "Data" },
  { value: "creditCard", label: "Credit Card", category: "Finance" },
  { value: "word", label: "Word", category: "Text" },
  { value: "sentence", label: "Sentence", category: "Text" },
  { value: "paragraph", label: "Paragraph", category: "Text" },
]

const defaultFields: Field[] = [
  { id: "1", name: "id", type: "uuid" },
  { id: "2", name: "name", type: "fullName" },
  { id: "3", name: "email", type: "email" },
  { id: "4", name: "createdAt", type: "date" },
]

export function MockDataGenerator() {
  const [fields, setFields] = useState<Field[]>(defaultFields)
  const [count, setCount] = useState(10)
  const [outputFormat, setOutputFormat] = useState<OutputFormat>("json")
  const [tableName, setTableName] = useState("users")
  const [output, setOutput] = useState("")

  const addField = () => {
    const newField: Field = {
      id: Math.random().toString(36).substring(7),
      name: `field${fields.length + 1}`,
      type: "word",
    }
    setFields([...fields, newField])
  }

  const removeField = (id: string) => {
    setFields(fields.filter((f) => f.id !== id))
  }

  const updateField = (id: string, updates: Partial<Field>) => {
    setFields(fields.map((f) => (f.id === id ? { ...f, ...updates } : f)))
  }

  const generateData = useCallback(() => {
    const data: Record<string, unknown>[] = []

    for (let i = 0; i < count; i++) {
      const row: Record<string, unknown> = {}
      for (const field of fields) {
        row[field.name] = generators[field.type]()
      }
      data.push(row)
    }

    let result: string

    if (outputFormat === "json") {
      result = JSON.stringify(data, null, 2)
    } else if (outputFormat === "csv") {
      const headers = fields.map((f) => f.name).join(",")
      const rows = data.map((row) =>
        fields.map((f) => {
          const val = row[f.name]
          if (typeof val === "string" && (val.includes(",") || val.includes('"'))) {
            return `"${val.replace(/"/g, '""')}"`
          }
          return String(val)
        }).join(",")
      )
      result = [headers, ...rows].join("\n")
    } else {
      // SQL INSERT
      const columns = fields.map((f) => f.name).join(", ")
      const inserts = data.map((row) => {
        const values = fields.map((f) => {
          const val = row[f.name]
          if (typeof val === "boolean") return val ? "TRUE" : "FALSE"
          if (typeof val === "number") return val
          return `'${String(val).replace(/'/g, "''")}'`
        }).join(", ")
        return `INSERT INTO ${tableName} (${columns}) VALUES (${values});`
      })
      result = inserts.join("\n")
    }

    setOutput(result)
  }, [fields, count, outputFormat, tableName])

  const copyOutput = () => {
    navigator.clipboard.writeText(output)
    toast.success("Copied to clipboard")
  }

  const downloadOutput = () => {
    const ext = outputFormat === "json" ? "json" : outputFormat === "csv" ? "csv" : "sql"
    const blob = new Blob([output], { type: "text/plain" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `mock-data.${ext}`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mock Data Generator</CardTitle>
        <CardDescription>
          Generate realistic fake data for testing. Define your schema and export as JSON, CSV, or SQL.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Fields Schema */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Fields</Label>
            <Button variant="outline" size="sm" onClick={addField}>
              <Plus className="h-4 w-4 mr-2" />
              Add Field
            </Button>
          </div>
          <div className="space-y-2">
            {fields.map((field) => (
              <div key={field.id} className="flex items-center gap-2">
                <Input
                  value={field.name}
                  onChange={(e) => updateField(field.id, { name: e.target.value })}
                  placeholder="Field name"
                  className="flex-1"
                />
                <Select value={field.type} onValueChange={(value: FieldType) => updateField(field.id, { type: value })}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(
                      fieldTypes.reduce((acc, ft) => {
                        if (!acc[ft.category]) acc[ft.category] = []
                        acc[ft.category].push(ft)
                        return acc
                      }, {} as Record<string, typeof fieldTypes>)
                    ).map(([category, types]) => (
                      <div key={category}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">{category}</div>
                        {types.map((ft) => (
                          <SelectItem key={ft.value} value={ft.value}>
                            {ft.label}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeField(field.id)}
                  disabled={fields.length <= 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Options */}
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="count">Number of Records</Label>
            <Input
              id="count"
              type="number"
              min={1}
              max={1000}
              value={count}
              onChange={(e) => setCount(Math.min(1000, Math.max(1, parseInt(e.target.value) || 1)))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="format">Output Format</Label>
            <Select value={outputFormat} onValueChange={(v: OutputFormat) => setOutputFormat(v)}>
              <SelectTrigger id="format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="json">JSON</SelectItem>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="sql">SQL INSERT</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {outputFormat === "sql" && (
            <div className="space-y-2">
              <Label htmlFor="tableName">Table Name</Label>
              <Input
                id="tableName"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                placeholder="users"
              />
            </div>
          )}
        </div>

        {/* Generate Button */}
        <Button onClick={generateData} className="w-full">
          <RefreshCw className="h-4 w-4 mr-2" />
          Generate {count} Records
        </Button>

        {/* Output */}
        {output && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Output</Label>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{output.split("\n").length} lines</Badge>
                <Button variant="ghost" size="sm" onClick={copyOutput}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
                <Button variant="ghost" size="sm" onClick={downloadOutput}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
            <Textarea
              value={output}
              readOnly
              className="font-mono text-sm min-h-[300px]"
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
